import { c as _c } from "react/compiler-runtime";
/**
 * useInfinitePrefetch — viewport-driven page lookahead for infinite lists.
 *
 * Replaces hand-rolled `IntersectionObserver` sentinels in consumer code with
 * the SDK's sanctioned `adapter.visibilityRef.observeElement` path (same path
 * the `<Items>` auto-load-more sentinel uses). One sentinel ref; the helper
 * fires `query.fetchNextPage()` once per sentinel-visibility transition,
 * gated on `hasNextPage && !isFetchingNextPage`.
 *
 * Tune the lookahead via `rootMargin` (default `'400px'` — twice the SDK
 * default so the next page is requested while the user is still scrolling).
 *
 * Usage:
 *
 *     const q = useGetThesesInfinite({ ... });
 *     const { sentinelRef } = useInfinitePrefetch(q);
 *     return (
 *       <>
 *         {q.rows.map((row) => <Row key={row.id} row={row} />)}
 *         <div ref={sentinelRef} aria-hidden />
 *       </>
 *     );
 *
 * Inside `<Items>` the infinite variant already wires its own
 * auto-load-more sentinel — prefer that. Use this helper only for lists
 * that render rows OUTSIDE `<Items>` (custom virtualization, etc.).
 */
import { useCallback } from 'react';
import { useSdkRuntime } from '../../provider/SdkRuntimeContext.js';
import { useLatestRef } from './useLatestRef.js';
export function useInfinitePrefetch(query, opts) {
  const $ = _c(6);
  const {
    adapter
  } = useSdkRuntime();
  const rootMargin = opts?.rootMargin ?? "400px";
  const latest = useLatestRef(query);
  let t0;
  if ($[0] !== adapter || $[1] !== latest || $[2] !== rootMargin) {
    t0 = el => {
      if (el === null) {
        return;
      }
      let firing = false;
      const unbind = adapter.visibilityRef.observeElement(el, intersecting => {
        if (!intersecting) {
          return;
        }
        const q = latest.current;
        if (!q.hasNextPage || q.isFetchingNextPage || q.isError === true || firing) {
          return;
        }
        firing = true;
        const result = q.fetchNextPage();
        const reset = () => {
          firing = false;
        };
        if (result instanceof Promise) {
          result.then(reset, reset);
        } else {
          reset();
        }
      }, {
        rootMargin
      });
      return unbind;
    };
    $[0] = adapter;
    $[1] = latest;
    $[2] = rootMargin;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const sentinelRef = t0;
  let t1;
  if ($[4] !== sentinelRef) {
    t1 = {
      sentinelRef
    };
    $[4] = sentinelRef;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}